// Executive Challenge "front office ledger" — the decision story behind the
// locked report card. Every entrant starts from the identical frozen roster,
// so the moves are where GMs actually diverge; this distills them into four
// superlatives for the result screen + share card:
//
//   bestMove  — the player YOU brought in (re-sign, signing, trade, draft)
//               who added the most wins for you
//   gotAway   — the player you let walk / traded away who produced the most
//               for somebody else
//   carried   — most impact on winning, however acquired (top wins added)
//   anchor    — the season's millstone: worst cumulative +/- with real minutes
//
// Pure: operates on plain player objects the caller loads, so it unit-tests
// without a database. "Wins added" is the engine's EWA stat — the most legible
// currency for a wins-based contest; raw +/- appears only on the anchor row,
// where its bluntness is the point.

import { PHASE } from "../../../common/constants.ts";

type LedgerTransaction = {
	season: number;
	phase: number;
	tid: number;
	type: string;
	fromTid?: number;
	pickNum?: number;
};

export type LedgerPlayer = {
	pid: number;
	firstName: string;
	lastName: string;
	transactions?: LedgerTransaction[];
	stats: {
		season: number;
		tid: number;
		playoffs?: boolean;
		ewa?: number;
		pm?: number;
		min?: number;
	}[];
	salaries: { season: number; amount: number }[];
};

export type LedgerChallenge = {
	tid: number;
	startSeason: number;
	startPhase: number;
	endSeason: number;
	startingPids?: number[];
};

export type LedgerRow = {
	pid: number;
	name: string;
	// How the player arrived (bestMove) or left (gotAway); undefined for the
	// roster superlatives (carried/anchor).
	how?: "resigned" | "signed" | "traded" | "drafted" | "walked";
	pickNum?: number;
	// Contract amount (thousands, engine units) for signings, when known.
	salary?: number;
	// Wins added (EWA) — for the challenge team, except gotAway, where it's
	// what they produced elsewhere.
	ewa?: number;
	// Cumulative season +/- (anchor only).
	pm?: number;
};

export type ChallengeLedger = {
	bestMove?: LedgerRow;
	gotAway?: LedgerRow;
	carried?: LedgerRow;
	anchor?: LedgerRow;
};

// Below this, a superlative is noise, not a story — omit the row.
const MIN_EWA = 0.5;

// The anchor must have actually played: a garbage-time rookie's −40 in 80
// minutes isn't the guy who dragged the season down.
const ANCHOR_MIN_MINUTES = 500;
const ANCHOR_MIN_MINUTES_FALLBACK = 200;

// A transaction inside the contested window: from the challenge's start phase
// (RESIGN_PLAYERS of startSeason — phases 7+ are the offseason tail of that
// game-year) through the endSeason draft. Nothing moves after finalize; the
// save is read-only.
const inWindow = (t: LedgerTransaction, ch: LedgerChallenge) =>
	(t.season === ch.startSeason && t.phase >= ch.startPhase) ||
	t.season === ch.endSeason;

const name = (p: LedgerPlayer) => `${p.firstName} ${p.lastName}`;

const round1 = (x: number) => Math.round(x * 10) / 10;

export const computeChallengeLedger = (
	players: LedgerPlayer[],
	ch: LedgerChallenge,
): ChallengeLedger => {
	type Digest = {
		p: LedgerPlayer;
		myEwa: number;
		myPm: number;
		myMin: number;
		elseEwa: number;
		windowTxs: LedgerTransaction[];
		lastArrivalAtMe: LedgerTransaction | undefined;
		wasMineAtStart: boolean;
	};

	const digests: Digest[] = players.map((p) => {
		let myEwa = 0;
		let myPm = 0;
		let myMin = 0;
		let elseEwa = 0;
		for (const row of p.stats) {
			if (row.season !== ch.endSeason || row.playoffs) {
				continue;
			}
			if (row.tid === ch.tid) {
				myEwa += row.ewa ?? 0;
				myPm += row.pm ?? 0;
				myMin += row.min ?? 0;
			} else {
				elseEwa += row.ewa ?? 0;
			}
		}
		const windowTxs = (p.transactions ?? []).filter((t) => inWindow(t, ch));
		return {
			p,
			myEwa,
			myPm,
			myMin,
			elseEwa,
			windowTxs,
			lastArrivalAtMe: windowTxs.findLast((t) => t.tid === ch.tid),
			wasMineAtStart: ch.startingPids?.includes(p.pid) ?? false,
		};
	});

	const ledger: ChallengeLedger = {};

	// bestMove: brought in during the window, most wins added for me. The
	// re-sign phase counts — keeping your own expiring star was a decision.
	const acquisitions = digests
		.filter((d) => d.lastArrivalAtMe && d.myEwa >= MIN_EWA)
		.sort((a, b) => b.myEwa - a.myEwa);
	const best = acquisitions[0];
	if (best) {
		const t = best.lastArrivalAtMe!;
		const how =
			t.type === "draft"
				? "drafted"
				: t.type === "trade"
					? "traded"
					: t.phase === PHASE.RESIGN_PLAYERS
						? "resigned"
						: "signed";
		ledger.bestMove = {
			pid: best.p.pid,
			name: name(best.p),
			how,
			pickNum: t.type === "draft" ? t.pickNum : undefined,
			salary:
				how === "signed" || how === "resigned"
					? best.p.salaries.find((s) => s.season === ch.endSeason)?.amount
					: undefined,
			ewa: round1(best.myEwa),
		};
	}

	// gotAway: their FINAL in-window move was away from my team (traded off, or
	// hit free agency and signed elsewhere), and they were mine to lose — on
	// the starting roster, or acquired by me earlier in the window. Ranked by
	// what they produced for everyone else.
	const departures = digests
		.filter((d) => {
			const last = d.windowTxs.at(-1);
			if (!last || last.tid === ch.tid || d.elseEwa < MIN_EWA) {
				return false;
			}
			const everMineInWindow = d.windowTxs.some((t) => t.tid === ch.tid);
			return d.wasMineAtStart || everMineInWindow;
		})
		.sort((a, b) => b.elseEwa - a.elseEwa);
	const away = departures[0];
	if (away) {
		const last = away.windowTxs.at(-1)!;
		ledger.gotAway = {
			pid: away.p.pid,
			name: name(away.p),
			how:
				last.type === "trade" && last.fromTid === ch.tid ? "traded" : "walked",
			ewa: round1(away.elseEwa),
		};
	}

	// carried: top wins added on my roster, however acquired.
	const carried = [...digests].sort((a, b) => b.myEwa - a.myEwa)[0];
	if (carried && carried.myEwa >= MIN_EWA) {
		ledger.carried = {
			pid: carried.p.pid,
			name: name(carried.p),
			ewa: round1(carried.myEwa),
		};
	}

	// anchor: worst cumulative +/- with real minutes. If nobody cleared the
	// floor (weird short rosters), relax once; if nobody was negative, there is
	// no millstone — omit rather than shame the least-positive starter.
	for (const floor of [ANCHOR_MIN_MINUTES, ANCHOR_MIN_MINUTES_FALLBACK]) {
		const anchor = digests
			.filter((d) => d.myMin >= floor && d.myPm < 0)
			.sort((a, b) => a.myPm - b.myPm)[0];
		if (anchor) {
			ledger.anchor = {
				pid: anchor.p.pid,
				name: name(anchor.p),
				pm: Math.round(anchor.myPm),
			};
			break;
		}
	}

	return ledger;
};

export default computeChallengeLedger;
